import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * 3-circle Venn diagram showing how the three progress reports overlap.
 * Hovering a circle highlights it; clicking the circle's heading or any
 * label inside it navigates to the relevant report.
 *
 * Visual logic:
 *  - Each circle is a translucent red disk; alpha stacks so the centre
 *    (all 3 reports overlap) is brightest, the pairwise lunes are mid,
 *    and the unique segments are darkest.
 *  - Labels are absolutely positioned HTML for crisp typography (SVG
 *    would lose Tailwind tokens).
 *  - On <md screens the Venn collapses to a stacked legend so the
 *    component remains useful on mobile.
 */

const REGIONS = {
    // Single-report only segments — link to the report's own section
    operationsOnly: [
        { label: "TOM definition", href: "/operations#summary" },
        { label: "BO Admin · Logistics", href: "/operations#workstreams" },
        { label: "Customer Support", href: "/operations#workstreams" },
        { label: "Service Operations", href: "/operations#workstreams" },
    ],
    businessOnly: [
        { label: "GTM strategy", href: "/business#pillars" },
        { label: "Sales pipeline", href: "/business#kpis" },
        { label: "Compensation model", href: "/business#capacity" },
        { label: "Investor framework", href: "/business#progress" },
    ],
    productItOnly: [
        { label: "MyFora platform", href: "/product-it#progress" },
        { label: "Pay at Table", href: "/product-it#priorities" },
        { label: "Digital onboarding", href: "/product-it#progress" },
        { label: "ERP planning", href: "/product-it#priorities" },
    ],
    // Pairwise overlaps — each link points to the most relevant report+section
    opsBiz: [
        { label: "GTM activation readiness", href: "/business#priorities" },
        { label: "Market launch governance", href: "/operations#dependencies" },
    ],
    opsPit: [
        { label: "Adyen integration", href: "/product-it#payment" },
        { label: "Tooling & AI quick wins", href: "/operations#workstreams" },
    ],
    bizPit: [
        { label: "HubSpot × Digital Onboarding", href: "/product-it#coordination" },
        { label: "Rep-level KPI tracking", href: "/business#kpis" },
    ],
    // Triple overlap — the truly cross-functional workstreams
    all: [
        { label: "CCHBC standardization", href: "/product-it#coordination" },
        { label: "International expansion", href: "/operations#priorities" },
    ],
};

const CoverageMap = () => {
    const [hovered, setHovered] = useState(null); // 'ops' | 'biz' | 'pit' | null

    const dim = (key) =>
        hovered && hovered !== key ? "opacity-25" : "opacity-100";

    return (
        <section
            className="relative mt-16 lg:mt-24 pt-12 lg:pt-16 border-t border-white/10"
            data-testid="coverage-map"
        >
            <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
                <div>
                    <div className="flex items-center gap-3">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            Coverage map
                        </span>
                    </div>
                    <h2
                        className="mt-4 font-[800] tracking-[-0.03em] leading-[1] text-3xl sm:text-4xl"
                        data-testid="coverage-map-title"
                    >
                        Where the reports
                        <br />
                        intersect.
                    </h2>
                </div>
                <p className="max-w-md text-white/55 text-sm leading-relaxed">
                    Hover any circle to spotlight its scope. Items in the
                    overlap zones are joint workstreams that span multiple
                    reports — the brightest centre captures the truly
                    cross-functional themes for 2026.
                </p>
            </div>

            {/* DESKTOP / TABLET — Venn diagram */}
            <div
                className="hidden md:block relative mx-auto"
                style={{ maxWidth: 720, height: 540 }}
                data-testid="venn-diagram"
            >
                {/* Operations circle (bottom-left) */}
                <Link
                    to="/operations"
                    onMouseEnter={() => setHovered("ops")}
                    onMouseLeave={() => setHovered(null)}
                    className={`absolute rounded-full border-2 border-[var(--fora-red-1)]/70 hover:border-[var(--fora-red-1)] transition-all cursor-pointer ${dim("ops")}`}
                    style={{
                        width: 360,
                        height: 360,
                        left: 30,
                        top: 150,
                        background:
                            "radial-gradient(circle at center, rgba(221, 38, 14, 0.55) 0%, rgba(221, 38, 14, 0.20) 60%, rgba(221, 38, 14, 0.08) 100%)",
                    }}
                    data-testid="venn-circle-operations"
                >
                    <span className="sr-only">Operations report</span>
                </Link>

                {/* Business circle (bottom-right) */}
                <Link
                    to="/business"
                    onMouseEnter={() => setHovered("biz")}
                    onMouseLeave={() => setHovered(null)}
                    className={`absolute rounded-full border-2 border-[var(--fora-red-1)]/70 hover:border-[var(--fora-red-1)] transition-all cursor-pointer ${dim("biz")}`}
                    style={{
                        width: 360,
                        height: 360,
                        left: 330,
                        top: 150,
                        background:
                            "radial-gradient(circle at center, rgba(221, 38, 14, 0.55) 0%, rgba(221, 38, 14, 0.20) 60%, rgba(221, 38, 14, 0.08) 100%)",
                    }}
                    data-testid="venn-circle-business"
                >
                    <span className="sr-only">Business report</span>
                </Link>

                {/* Product & IT circle (top-centre) */}
                <Link
                    to="/product-it"
                    onMouseEnter={() => setHovered("pit")}
                    onMouseLeave={() => setHovered(null)}
                    className={`absolute rounded-full border-2 border-[var(--fora-red-1)]/70 hover:border-[var(--fora-red-1)] transition-all cursor-pointer ${dim("pit")}`}
                    style={{
                        width: 360,
                        height: 360,
                        left: 180,
                        top: 0,
                        background:
                            "radial-gradient(circle at center, rgba(221, 38, 14, 0.55) 0%, rgba(221, 38, 14, 0.20) 60%, rgba(221, 38, 14, 0.08) 100%)",
                    }}
                    data-testid="venn-circle-product-it"
                >
                    <span className="sr-only">Product & IT report</span>
                </Link>

                {/* CIRCLE TITLES (clickable) */}
                <Link
                    to="/operations"
                    className={`absolute text-[10px] tracking-[0.22em] uppercase text-white font-bold pointer-events-auto hover:text-[var(--fora-red-1)] transition-colors ${dim("ops")}`}
                    style={{ left: 8, top: 195 }}
                >
                    Operations
                </Link>
                <Link
                    to="/business"
                    className={`absolute text-[10px] tracking-[0.22em] uppercase text-white font-bold pointer-events-auto hover:text-[var(--fora-red-1)] transition-colors ${dim("biz")}`}
                    style={{ right: 8, top: 195, textAlign: "right" }}
                >
                    Business
                </Link>
                <Link
                    to="/product-it"
                    className={`absolute text-[10px] tracking-[0.22em] uppercase text-white font-bold pointer-events-auto hover:text-[var(--fora-red-1)] transition-colors ${dim("pit")}`}
                    style={{ left: "50%", transform: "translateX(-50%)", top: 6 }}
                >
                    Product &amp; IT
                </Link>

                {/* SEGMENT LABELS — Operations only (far left) */}
                <Region
                    items={REGIONS.operationsOnly}
                    style={{ left: 30, top: 320, width: 150 }}
                    align="left"
                    dimmed={hovered && hovered !== "ops"}
                    testId="venn-region-ops-only"
                />

                {/* Business only (far right) */}
                <Region
                    items={REGIONS.businessOnly}
                    style={{ right: 30, top: 320, width: 150 }}
                    align="right"
                    dimmed={hovered && hovered !== "biz"}
                    testId="venn-region-biz-only"
                />

                {/* Product & IT only (top centre) */}
                <Region
                    items={REGIONS.productItOnly}
                    style={{ left: 290, top: 50, width: 140 }}
                    align="center"
                    dimmed={hovered && hovered !== "pit"}
                    testId="venn-region-pit-only"
                />

                {/* All 3 (true centre) — also adds a subtle highlight glow
                   so the centre overlap region pops visually. */}
                <div
                    className="absolute rounded-full bg-[var(--fora-red-1)]/30 pointer-events-none"
                    style={{
                        width: 110,
                        height: 110,
                        left: 305,
                        top: 280,
                    }}
                    aria-hidden="true"
                />
                <Region
                    items={REGIONS.all}
                    style={{ left: 290, top: 295, width: 140 }}
                    align="center"
                    dimmed={false}
                    testId="venn-region-all"
                    highlight
                />

                {/* Ops ∩ Business (bottom centre, shifted lower so it doesn't
                   collide with the centre label) */}
                <Region
                    items={REGIONS.opsBiz}
                    style={{ left: 270, top: 445, width: 180 }}
                    align="center"
                    dimmed={hovered && hovered === "pit"}
                    testId="venn-region-ops-biz"
                    accent
                />

                {/* Ops ∩ PIT (left-centre) */}
                <Region
                    items={REGIONS.opsPit}
                    style={{ left: 195, top: 240, width: 140 }}
                    align="center"
                    dimmed={hovered && hovered === "biz"}
                    testId="venn-region-ops-pit"
                    accent
                />

                {/* Business ∩ PIT (right-centre) */}
                <Region
                    items={REGIONS.bizPit}
                    style={{ left: 385, top: 240, width: 140 }}
                    align="center"
                    dimmed={hovered && hovered === "ops"}
                    testId="venn-region-biz-pit"
                    accent
                />

                {/* All 3 (true centre) — moved into glow blob defined above */}
            </div>

            {/* MOBILE — collapsed legend */}
            <div className="md:hidden grid gap-3" data-testid="venn-mobile-legend">
                <MobileBlock
                    label="All three"
                    items={REGIONS.all}
                    accent
                    href={null}
                    testId="venn-mobile-all"
                />
                <MobileBlock
                    label="Operations × Business"
                    items={REGIONS.opsBiz}
                    href={null}
                    testId="venn-mobile-ops-biz"
                />
                <MobileBlock
                    label="Operations × Product & IT"
                    items={REGIONS.opsPit}
                    href={null}
                    testId="venn-mobile-ops-pit"
                />
                <MobileBlock
                    label="Business × Product & IT"
                    items={REGIONS.bizPit}
                    href={null}
                    testId="venn-mobile-biz-pit"
                />
                <MobileBlock
                    label="Operations only"
                    items={REGIONS.operationsOnly}
                    href="/operations"
                    testId="venn-mobile-ops"
                />
                <MobileBlock
                    label="Business only"
                    items={REGIONS.businessOnly}
                    href="/business"
                    testId="venn-mobile-biz"
                />
                <MobileBlock
                    label="Product & IT only"
                    items={REGIONS.productItOnly}
                    href="/product-it"
                    testId="venn-mobile-pit"
                />
            </div>
        </section>
    );
};

const Region = ({ items, style, align = "center", dimmed, testId, accent, highlight }) => {
    const alignClass =
        align === "left"
            ? "text-left"
            : align === "right"
              ? "text-right"
              : "text-center";
    return (
        <div
            className={`absolute z-10 transition-opacity duration-300 ${
                dimmed ? "opacity-30" : "opacity-100"
            } ${alignClass}`}
            style={style}
            data-testid={testId}
        >
            <ul className="space-y-1">
                {items.map((it, i) => {
                    const baseClass = `text-[10px] sm:text-[11px] leading-tight font-semibold tracking-[-0.005em] ${
                        highlight
                            ? "text-white"
                            : accent
                              ? "text-white/90"
                              : "text-white/85"
                    }`;
                    return (
                        <li key={i}>
                            {it.href ? (
                                <Link
                                    to={it.href}
                                    className={`${baseClass} hover:text-[var(--fora-red-1)] hover:underline underline-offset-4 decoration-[var(--fora-red-1)]/70 transition-colors inline-block`}
                                    data-testid={`${testId}-item-${i}`}
                                >
                                    {it.label}
                                </Link>
                            ) : (
                                <span className={baseClass}>{it.label}</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const MobileBlock = ({ label, items, accent, highlight, href, testId }) => {
    const Wrapper = href ? Link : "div";
    return (
        <Wrapper
            to={href || undefined}
            className={`p-4 border transition-colors ${
                highlight
                    ? "bg-[var(--fora-red-1)]/15 border-[var(--fora-red-1)]"
                    : accent
                      ? "bg-white/[0.04] border-white/20"
                      : "bg-black/40 border-white/10"
            } ${href ? "hover:border-[var(--fora-red-1)] block" : "block"}`}
            data-testid={testId}
        >
            <div className="text-[9px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                {label}
            </div>
            <ul className="mt-2 space-y-1">
                {items.map((it, i) => (
                    <li
                        key={i}
                        className="text-xs text-white/85 leading-snug"
                    >
                        {it.href ? (
                            <Link
                                to={it.href}
                                className="hover:text-[var(--fora-red-1)] inline-flex items-center gap-1"
                                data-testid={`${testId}-item-${i}`}
                                onClick={(e) => e.stopPropagation()}
                            >
                                · {it.label} →
                            </Link>
                        ) : (
                            <>· {it.label}</>
                        )}
                    </li>
                ))}
            </ul>
        </Wrapper>
    );
};

export default CoverageMap;
